// @flow
import React from 'react';
import { Grid, Label, Table } from 'semantic-ui-react';
import { getIn, useFormikContext } from 'formik';

import { CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG } from '../../../constants';
import {
  StyledCheckboxWithAccessibility,
  StyledFormikErrorMessage,
  StyledMonthlyUsageInput,
} from '../../../common-utils/styledComponents';
import { Field } from '../../../formik-utils/formikSUIWrapper';

import { addAdditionalDummyMonthsIfRequired } from './utils';
import ClusterMonthlyUsageRampCalculator from './ClusterMonthlyUsageRampCalculator';
import { PeakLessThanAvgPopup } from './PeakLessThanAvgPopup';

export const ClusterInputTableRow = ({
  displayName,
  backendName,
  selectedYear,
  months,
  onChangeFunc,
  isBooleanField = false,
  monthlyEstimatesFieldName,
  displayNameWidth = 4,
  inputWidth = 1,
  totalNumberOfMonths,
  disabled,
  isReadOnly = false,
  shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled = false,
}) => {
  const monthsToUse = addAdditionalDummyMonthsIfRequired(selectedYear, months);
  const { values: formikValues } = useFormikContext();

  return (
    <Table.Row disabled={disabled}>
      {isBooleanField ? (
        <Table.Cell width={displayNameWidth}>{displayName}</Table.Cell>
      ) : (
        <Table.Cell>
          <Grid>
            <Grid.Row>
              <Grid.Column width={isReadOnly ? 16 : 12}>{displayName}</Grid.Column>
              {!isReadOnly && (
                <Grid.Column textAlign="right" width={4}>
                  <ClusterMonthlyUsageRampCalculator
                    monthlyEstimatesFieldName={monthlyEstimatesFieldName}
                    totalNumberOfMonths={totalNumberOfMonths}
                    usageMetric={backendName}
                  />
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
        </Table.Cell>
      )}

      {monthsToUse.map((month, index) => {
        const fieldName = `${monthlyEstimatesFieldName}[months][${month.name}].${backendName}`;

        let isPeakLessThanAvgForMonth = false;
        if (fieldName.includes('_peak_')) {
          const correspondingAvgFieldName = fieldName.replace('_peak_', '_avg_');
          const peakValue = getIn(formikValues, fieldName);
          const averageValue = getIn(formikValues, correspondingAvgFieldName);
          isPeakLessThanAvgForMonth = peakValue < averageValue;
        }

        const isKafkaDisabledForMonth = !getIn(
          formikValues,
          `${CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG}[months][${month.name}].is_enabled`
        );

        const shouldMonthBeDisabledBasedOnKafkaMonth =
          shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled && isKafkaDisabledForMonth;
        // Note, when shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled is true, then, check if the corresponding Kafka month is disabled, if yes, we need to disable this field
        // Note, just disabling the field here and not setting the checkbox value to false that is handled by getDataForOtherTabsThatNeedToBeSentToBackEnd

        // todo:: Use CheckBoxField and TextField here - not used currently because of some CSS issue with FormField

        return (
          <Table.Cell
            disabled={disabled}
            key={index}
            selectable={!isReadOnly}
            style={
              isPeakLessThanAvgForMonth
                ? {
                    border: '2px dotted red',
                  }
                : {}
            }
            textAlign="center"
            width={inputWidth}
          >
            {isReadOnly ? (
              <>{getIn(formikValues, fieldName)}</>
            ) : (
              <>
                {isBooleanField ? (
                  <>
                    <Field
                      component={StyledCheckboxWithAccessibility}
                      disabled={
                        month.isMonthArtificiallyGenerated === true ||
                        shouldMonthBeDisabledBasedOnKafkaMonth ||
                        disabled
                      }
                      name={fieldName}
                      onChange={(event, value, setFieldValue, setFieldTouched, values, checked) =>
                        onChangeFunc(
                          month.name,
                          event,
                          value,
                          setFieldValue,
                          setFieldTouched,
                          values,
                          checked
                        )
                      }
                      toggle={true}
                      type="checkbox"
                    />
                    <StyledFormikErrorMessage
                      basic={true}
                      color="red"
                      component={Label}
                      name={fieldName}
                      pointing={true}
                      showErrorsOnlyWhenTouched={false}
                    />
                  </>
                ) : (
                  <>
                    <Field
                      component={StyledMonthlyUsageInput}
                      disabled={month.isMonthArtificiallyGenerated === true || disabled}
                      name={fieldName}
                      onChange={onChangeFunc}
                      onFocus={(e) => e.target.select()}
                      type="number"
                    />
                    {isPeakLessThanAvgForMonth && <PeakLessThanAvgPopup />}
                    <StyledFormikErrorMessage
                      basic={true}
                      color="red"
                      component={Label}
                      name={fieldName}
                      pointing={true}
                      showErrorsOnlyWhenTouched={false}
                    />
                  </>
                )}
              </>
            )}
          </Table.Cell>
        );
      })}
    </Table.Row>
  );
};
